import React, { useEffect, useMemo } from 'react';
import {
	getSensorGroups,
	useSelectPaginatedSensorGroups,
} from '@innovyze/stylovyze';
import { useDispatch, useSelector } from 'react-redux';

import { FacilityPropertiesDetails } from './FacilityPropertiesDetails';
import { FeatureItem } from '../../utils';
import { getFacility } from '../../../../redux/facility/actions';
import {
	selectFacilityById,
	selectFacilitySensors,
} from '../../../../redux/facility/selectors';

const getSensorGroupName = (
	sensorGroupList: Array<{ _id: string; name?: string }>,
	sensorGroupId: string,
): string => {
	const sensorGroup = sensorGroupList.find(
		item => item._id === sensorGroupId,
	);
	return sensorGroup?.name ?? sensorGroupId;
};

const getWorkspaceType = (
	facilityType?: string,
): 'Pump Station' | 'Facility' => {
	// Treat anything that isn't a pump station as a Facility
	if (facilityType && facilityType !== 'Pump Station') return 'Facility';
	return 'Pump Station';
};

interface FacilityPropertiesProps {
	item: FeatureItem;
}

export const FacilityProperties = ({
	item,
}: FacilityPropertiesProps): JSX.Element => {
	const dispatch = useDispatch();
	const facilityId = item.id;
	const facilityName = (item.name as string) ?? facilityId;

	const facility = useSelector(state =>
		selectFacilityById(state, facilityId),
	);

	const sensors = useSelector(state =>
		selectFacilitySensors(state, facilityId),
	);

	const {
		status: sensorGroupsInitialized,
		sensorGroups,
	} = useSelectPaginatedSensorGroups();

	useEffect(() => {
		if (!facility) dispatch(getFacility(facilityId));
	}, [facility]);

	useEffect(() => {
		if (sensorGroupsInitialized !== 'success')
			dispatch(
				getSensorGroups({
					limit: 1000,
					includeSensorGroupPairs: 'true',
				}),
			);
	}, []);

	const namedSensorGroupList = useMemo(() => {
		if (facility) {
			if (sensorGroups)
				return facility.sensorGroups.map(sensorGroup =>
					getSensorGroupName(sensorGroups, sensorGroup),
				);
			else return facility.sensorGroups;
		}
		return [];
	}, [sensorGroups, facility]);

	const isFacilityPropertiesInitialized = !!facility;

	const workspaceType = useMemo(() => {
		return getWorkspaceType(item.facilityType as string | undefined);
	}, [item.facilityType]);

	const imageSrc = facility ? facility.imageSrc : undefined;

	return (
		<FacilityPropertiesDetails
			initialized={isFacilityPropertiesInitialized}
			facilityName={facilityName}
			workspaceType={workspaceType}
			workspaceId={item.workspaceId as string | undefined}
			imageSrc={imageSrc}
			sensorGroups={namedSensorGroupList}
			sensors={facility ? sensors : []}
		/>
	);
};
